import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/authStorage';
import axios from 'axios';
import {
  HiOutlineSun, HiOutlineMoon, HiCheck, HiX, HiOutlineCheckCircle,
  HiOutlineUserGroup, HiOutlineStar, HiOutlinePhone, HiOutlineVideoCamera,
  HiOutlineCurrencyDollar, HiOutlineGift, HiOutlineCash, HiOutlinePlus,
  HiOutlineAdjustments, HiOutlineBadgeCheck, HiOutlineLightBulb, HiOutlineSparkles,
} from 'react-icons/hi';
import ConfirmModal from '../components/ConfirmModal';
import './AdminPanel.css';
import './Profile.css';

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGender, setFilterGender] = useState('ALL');
  const [filterVerified, setFilterVerified] = useState('ALL');
  const [sortBy, setSortBy] = useState('subscription'); // name, subscription, credits, joined
  const [activeTab, setActiveTab] = useState('requests');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToVerify, setUserToVerify] = useState(null);
  const [userToRevoke, setUserToRevoke] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [userToCredit, setUserToCredit] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsAction, setCreditsAction] = useState('add'); // 'add' or 'set'
  const [approving, setApproving] = useState(false);
  
  // Earnings State
  const [earnings, setEarnings] = useState([]);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [earningToPayout, setEarningToPayout] = useState(null);
  const [processingPayout, setProcessingPayout] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState('');
  const [payoutDetails, setPayoutDetails] = useState('');

  // Confirm/prompt modals (replacing native window.confirm/prompt)
  const [requestToApprove, setRequestToApprove] = useState(null);
  const [approvingRequest, setApprovingRequest] = useState(false);
  const [requestToReject, setRequestToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingRequest, setRejectingRequest] = useState(false);
  const [adminToDeactivate, setAdminToDeactivate] = useState(null);
  const [deactivatingAdmin, setDeactivatingAdmin] = useState(false);

  // Admin Management State
  const [admins, setAdmins] = useState([]);
  const [showInviteAdminModal, setShowInviteAdminModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('MODERATOR');
  const [inviteTempPassword, setInviteTempPassword] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [userToPromote, setUserToPromote] = useState(null);
  
  // Bonus Credit State
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [userToBonus, setUserToBonus] = useState(null);
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusDescription, setBonusDescription] = useState('');
  const [creditingBonus, setCreditingBonus] = useState(false);
  const [promoteRole, setPromoteRole] = useState('MODERATOR');
  const [promoting, setPromoting] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Calculate dashboard stats (excluding deactivated users)
  const activeUsersList = users.filter(u => !u.deactivated);
  const stats = {
    totalUsers: activeUsersList.length,
    maleUsers: activeUsersList.filter(u => u.profile?.gender === 'MALE').length,
    femaleUsers: activeUsersList.filter(u => u.profile?.gender === 'FEMALE').length,
    verifiedUsers: activeUsersList.filter(u => u.hasVerifiedBadge).length,
    activeUsers: activeUsersList.filter(u => u.active).length,
    verificationRequests: verificationRequests.length,
    phoneVerified: activeUsersList.filter(u => u.phoneNumber).length,
    emailVerified: activeUsersList.filter(u => u.emailVerified).length,
    fullyVerified: activeUsersList.filter(u => u.phoneNumber && u.emailVerified).length,
    creators: activeUsersList.filter(u => u.profile?.isCreator).length,
  };

  const genderRatio = stats.totalUsers > 0 
    ? {
        male: Math.round((stats.maleUsers / stats.totalUsers) * 100),
        female: Math.round((stats.femaleUsers / stats.totalUsers) * 100)
      }
    : { male: 0, female: 0 };

  useEffect(() => {
    loadUsers();
    loadVerificationRequests();
    loadAdmins();
    loadEarnings();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadVerificationRequests = async () => {
    try {
      const token = getToken();
      console.log('Loading verification requests from:', `${API_URL}/verification/pending`);
      console.log('Token exists:', !!token);
      
      const response = await axios.get(`${API_URL}/verification/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Verification requests response:', response.data);
      console.log('Response is array:', Array.isArray(response.data));
      console.log('Response length:', Array.isArray(response.data) ? response.data.length : 'N/A');
      
      // Sort by requestedAt date, most recent first
      const sortedRequests = Array.isArray(response.data) 
        ? response.data.sort((a, b) => {
            const dateA = new Date(a.requestedAt || 0);
            const dateB = new Date(b.requestedAt || 0);
            return dateB - dateA; // Descending order (newest first)
          })
        : [];
      
      setVerificationRequests(sortedRequests);
      console.log('Set verification requests:', sortedRequests.length);
    } catch (err) {
      console.error('Failed to load verification requests:', err);
      console.error('Error status:', err.response?.status);
      console.error('Error details:', err.response?.data);
      console.error('Error message:', err.message);
      
      // Show error to user if it's an auth issue
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      }
    }
  };

  const handleApproveRequest = (requestId) => {
    setRequestToApprove(requestId);
  };

  const confirmApproveRequest = async () => {
    if (!requestToApprove) return;
    setApprovingRequest(true);
    try {
      const token = getToken();
      await axios.post(`${API_URL}/verification/approve/${requestToApprove}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Verification request approved!');
      setTimeout(() => setSuccess(''), 3000);
      setRequestToApprove(null);
      loadVerificationRequests();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve request');
      setTimeout(() => setError(''), 3000);
    } finally {
      setApprovingRequest(false);
    }
  };

  const handleRejectRequest = (requestId) => {
    setRejectReason('');
    setRequestToReject(requestId);
  };

  const confirmRejectRequest = async (reason) => {
    if (!requestToReject || !reason || !reason.trim()) return;
    setRejectingRequest(true);
    try {
      const token = getToken();
      await axios.post(`${API_URL}/verification/reject/${requestToReject}`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSuccess('Verification request rejected');
      setTimeout(() => setSuccess(''), 3000);
      setRequestToReject(null);
      setRejectReason('');
      loadVerificationRequests();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject request');
      setTimeout(() => setError(''), 3000);
    } finally {
      setRejectingRequest(false);
    }
  };

  const handleApproveVerification = async (userId) => {
    setApproving(true);
    try {
      const token = getToken();
      await axios.post(`${API_URL}/admin/users/${userId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('User verified successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setShowVerificationModal(false);
      setUserToVerify(null);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify user');
      setTimeout(() => setError(''), 3000);
    } finally {
      setApproving(false);
    }
  };

  const handleRevokeVerification = async (userId) => {
    setApproving(true);
    try {
      const token = getToken();
      await axios.post(`${API_URL}/admin/users/${userId}/unverify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Verification badge removed');
      setTimeout(() => setSuccess(''), 3000);
      setShowRevokeModal(false);
      setUserToRevoke(null);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke verification');
      setTimeout(() => setError(''), 3000);
    } finally {
      setApproving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setApproving(true);
    try {
      const token = getToken();
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('User deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      setShowDeleteModal(false);
      setUserToDelete(null);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
      setTimeout(() => setError(''), 3000);
    } finally {
      setApproving(false);
    }
  };

  const handleManageCredits = async () => {
    if (!creditsAmount || isNaN(creditsAmount) || parseInt(creditsAmount) < 0) {
      setError('Please enter a valid number');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setApproving(true);
    try {
      const token = getToken();
      const endpoint = creditsAction === 'add' ? 'add-credits' : 'set-credits';
      
      await axios.post(
        `${API_URL}/admin/users/${userToCredit.id}/${endpoint}`,
        { credits: parseInt(creditsAmount) },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const action = creditsAction === 'add' ? 'added' : 'set';
      setSuccess(`Credits ${action} successfully!`);
      setTimeout(() => setSuccess(''), 3000);
      setShowCreditsModal(false);
      setUserToCredit(null);
      setCreditsAmount('');
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to update credits');
      setTimeout(() => setError(''), 3000);
    } finally {
      setApproving(false);
    }
  };
  
  const handleCreditBonus = async () => {
    if (!bonusAmount || parseInt(bonusAmount) <= 0) {
      setError('Please enter a valid bonus amount');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    if (!bonusDescription || bonusDescription.trim() === '') {
      setError('Please enter a description for the bonus');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setCreditingBonus(true);
    try {
      const token = getToken();
      
      await axios.post(
        `${API_URL}/admin/users/${userToBonus.id}/credit-bonus`,
        { 
          amount: parseInt(bonusAmount),
          description: bonusDescription
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setSuccess(`Bonus of ${bonusAmount} credits credited successfully!`);
      setTimeout(() => setSuccess(''), 3000);
      setShowBonusModal(false);
      setUserToBonus(null);
      setBonusAmount('');
      setBonusDescription('');
      loadEarnings(); // Refresh earnings to show the new bonus
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to credit bonus');
      setTimeout(() => setError(''), 3000);
    } finally {
      setCreditingBonus(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      const token = getToken();
      await axios.post(`${API_URL}/admin/users/${userId}/toggle-active`, 
        { active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSuccess(`User ${!currentStatus ? 'activated' : 'suspended'}`);
      setTimeout(() => setSuccess(''), 3000);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user status');
      setTimeout(() => setError(''), 3000);
    }
  };

  const filteredUsers = users.filter(user => {
    // Exclude deactivated (soft deleted) users
    if (user.deactivated) {
      return false;
    }
    
    const matchesSearch = !searchTerm || 
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.phoneNumber && user.phoneNumber.includes(searchTerm)) ||
      (user.profile?.name && user.profile.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesGender = filterGender === 'ALL' || user.profile?.gender === filterGender;
    const matchesVerified = filterVerified === 'ALL' || 
      (filterVerified === 'VERIFIED' && user.hasVerifiedBadge) ||
      (filterVerified === 'UNVERIFIED' && !user.hasVerifiedBadge);
    
    return matchesSearch && matchesGender && matchesVerified;
  }).sort((a, b) => {
    // Sorting logic
    switch (sortBy) {
      case 'name':
        const nameA = a.profile?.name || '';
        const nameB = b.profile?.name || '';
        return nameA.localeCompare(nameB);
      
      case 'credits':
        const creditsA = a.credits || 0;
        const creditsB = b.credits || 0;
        return creditsB - creditsA; // Descending order
      
      case 'joined':
        return new Date(b.createdAt) - new Date(a.createdAt); // Newest first
      
      default:
        return 0;
    }
  });

  // Admin Management Functions
  const loadAdmins = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load admins:', err);
    }
  };
  
  const loadEarnings = async () => {
    setLoadingEarnings(true);
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/admin/earnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEarnings(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load earnings:', err);
      setEarnings([]);
    } finally {
      setLoadingEarnings(false);
    }
  };

  const handleInviteAdmin = async () => {
    if (!inviteEmail || !inviteTempPassword) {
      setError('Email and temporary password are required');
      return;
    }

    setInviting(true);
    try {
      const token = getToken();
      await axios.post(`${API_URL}/admin/invite-admin`, {
        email: inviteEmail,
        role: inviteRole,
        tempPassword: inviteTempPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Admin invited successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setShowInviteAdminModal(false);
      setInviteEmail('');
      setInviteTempPassword('');
      setInviteRole('MODERATOR');
      loadAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to invite admin');
      setTimeout(() => setError(''), 3000);
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateAdminRole = async (adminId, newRole) => {
    try {
      const token = getToken();
      await axios.put(`${API_URL}/admin/admins/${adminId}/role`, {
        role: newRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Admin role updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update admin role');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeactivateAdmin = (adminId) => {
    setAdminToDeactivate(adminId);
  };

  const confirmDeactivateAdmin = async () => {
    if (!adminToDeactivate) return;
    setDeactivatingAdmin(true);
    try {
      const token = getToken();
      await axios.delete(`${API_URL}/admin/admins/${adminToDeactivate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Admin deactivated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setAdminToDeactivate(null);
      loadAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate admin');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeactivatingAdmin(false);
    }
  };

  const handlePromoteUser = async () => {
    if (!userToPromote) return;

    setPromoting(true);
    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/admin/users/${userToPromote.id}/promote`,
        { role: promoteRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(`User promoted to ${promoteRole} successfully!`);
      setTimeout(() => setSuccess(''), 3000);
      setShowPromoteModal(false);
      setUserToPromote(null);
      setPromoteRole('MODERATOR');
      loadUsers();
      loadAdmins(); // Refresh admin list too
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to promote user');
      setTimeout(() => setError(''), 3000);
    } finally {
      setPromoting(false);
    }
  };
  
  const handlePayoutEarning = async () => {
    if (!earningToPayout) return;

    if (!payoutMethod.trim() || !payoutDetails.trim()) {
      setError('Please enter both a payout method and payout details.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setProcessingPayout(true);
    try {
      const token = getToken();
      await axios.post(
        `${API_URL}/admin/earnings/${earningToPayout.id}/payout`,
        { payoutMethod, payoutDetails },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Payout processed successfully!');
      setTimeout(() => setSuccess(''), 3000);
      setShowPayoutModal(false);
      setEarningToPayout(null);
      setPayoutMethod('');
      setPayoutDetails('');
      loadEarnings(); // Refresh earnings
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process payout');
      setTimeout(() => setError(''), 3000);
    } finally {
      setProcessingPayout(false);
    }
  };

  return (
    <div className="profile-container admin-panel" style={{ background: 'var(--bg-primary)' }}>
      {/* Top Navigation Bar */}
      <div className="admin-topnav" style={{ 
        background: 'var(--card-bg)',
        borderBottom: '2px solid var(--border-color)',
        padding: '16px 40px',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '32px', 
                height: '32px', 
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '900',
                color: 'white',
                fontSize: '16px'
              }}>K</div>
              <span style={{ fontSize: '18px', fontWeight: '800' }}>KwikLuv Admin</span>
            </div>
            <nav style={{ display: 'flex', gap: '8px' }}>
              <a 
                href="/discover" 
                style={{ 
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 107, 107, 0.1)';
                  e.target.style.color = '#ff6b6b';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'var(--text-secondary)';
                }}
              >
                Browse
              </a>
              <a 
                href="/inbox" 
                style={{ 
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 107, 107, 0.1)';
                  e.target.style.color = '#ff6b6b';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'var(--text-secondary)';
                }}
              >
                Messages
              </a>
              <a 
                href="/profile" 
                style={{ 
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 107, 107, 0.1)';
                  e.target.style.color = '#ff6b6b';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'var(--text-secondary)';
                }}
              >
                Profile
              </a>
            </nav>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => setDarkMode(!darkMode)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                background: 'transparent',
                border: '1px solid var(--border-color)',
                color: 'var(--text-primary)',
                fontSize: '18px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 107, 107, 0.1)'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              aria-label={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? <HiOutlineSun /> : <HiOutlineMoon />}
            </button>
            <div style={{ 
              padding: '8px 16px',
              borderRadius: '8px',
              background: 'rgba(255, 107, 107, 0.1)',
              border: '1px solid rgba(255, 107, 107, 0.3)',
              fontSize: '13px',
              fontWeight: '700',
              color: '#ff6b6b'
            }}>
              ADMIN
            </div>
            <button
              onClick={() => window.location.href = '/discover'}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                background: '#ff6b6b',
                border: 'none',
                color: 'white',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#ee5a6f'}
              onMouseLeave={(e) => e.target.style.background = '#ff6b6b'}
            >
              Exit Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Header */}
      <div className="admin-header-bar" style={{ 
        background: 'var(--card-bg)',
        borderBottom: '1px solid var(--border-color)',
        padding: '32px 40px',
        marginBottom: '32px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '32px', fontWeight: '800' }}>
              Dashboard Overview
            </h1>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '15px' }}>
              Monitor platform activity and manage users
            </p>
          </div>

          {/* Stats Grid */}
          <div className="admin-stats-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '20px',
            marginBottom: '32px'
          }}>
            <div style={{ 
              padding: '20px', 
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '600' }}>
                VERIFICATION REQUESTS
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#8b5cf6', marginBottom: '4px' }}>
                {stats.verificationRequests}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Badge requests
              </div>
            </div>

            <div style={{ 
              padding: '20px', 
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '600' }}>
                TOTAL USERS
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#3b82f6', marginBottom: '4px' }}>
                {stats.totalUsers}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {stats.activeUsers} active
              </div>
            </div>

            <div style={{ 
              padding: '20px', 
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
              border: '2px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '600' }}>
                VERIFIED BADGES
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#22c55e', marginBottom: '4px' }}>
                {stats.verifiedUsers}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                {stats.phoneVerified} phone | {stats.emailVerified} email
              </div>
            </div>

            <div style={{ 
              padding: '20px', 
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(236, 72, 153, 0.05) 100%)',
              border: '2px solid rgba(236, 72, 153, 0.3)',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: '600' }}>
                PAID SUBSCRIBERS
              </div>
              <div style={{ fontSize: '32px', fontWeight: '800', color: '#ec4899', marginBottom: '4px' }}>
                {stats.paidSubscribers}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Creators
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="admin-charts-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
            {/* Gender Distribution Chart */}
            <div style={{ 
              padding: '24px', 
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px'
            }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '700' }}>
                Gender Distribution
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                <div style={{ flex: 1 }}>
                  {/* Bar Chart */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#3b82f6' }}>Male</span>
                      <span style={{ fontSize: '13px', fontWeight: '700' }}>{stats.maleUsers} ({genderRatio.male}%)</span>
                    </div>
                    <div style={{ 
                      height: '12px', 
                      background: 'rgba(59, 130, 246, 0.1)', 
                      borderRadius: '6px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ 
                        height: '100%', 
                        width: `${genderRatio.male}%`, 
                        background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                        transition: 'width 0.5s ease'
                      }} />
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#ec4899' }}>Female</span>
                      <span style={{ fontSize: '13px', fontWeight: '700' }}>{stats.femaleUsers} ({genderRatio.female}%)</span>
                    </div>
                    <div style={{ 
                      height: '12px', 
                      background: 'rgba(236, 72, 153, 0.1)', 
                      borderRadius: '6px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ 
                        height: '100%', 
                        width: `${genderRatio.female}%`, 
                        background: 'linear-gradient(90deg, #ec4899 0%, #db2777 100%)',
                        transition: 'width 0.5s ease'
                      }} />
                    </div>
                  </div>
                </div>
                {/* Donut Chart */}
                <div style={{ position: 'relative', width: '120px', height: '120px' }}>
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="rgba(59, 130, 246, 0.2)"
                      strokeWidth="20"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="20"
                      strokeDasharray={`${genderRatio.male * 3.14} ${314 - genderRatio.male * 3.14}`}
                      strokeDashoffset="0"
                      transform="rotate(-90 60 60)"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#ec4899"
                      strokeWidth="20"
                      strokeDasharray={`${genderRatio.female * 3.14} ${314 - genderRatio.female * 3.14}`}
                      strokeDashoffset={`-${genderRatio.male * 3.14}`}
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: '800' }}>{stats.totalUsers}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Total</div>
                  </div>
                </div>
              </div>
            </div>

            {/* User Status Chart */}
            <div style={{ 
              padding: '24px', 
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '16px'
            }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '700' }}>
                User Status Overview
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '12px',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '2px solid rgba(34, 197, 94, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '900',
                    fontSize: '18px',
                    color: '#22c55e'
                  }}><HiCheck /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Active Users
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#22c55e' }}>
                      {stats.activeUsers}
                    </div>
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-secondary)' }}>
                    {stats.totalUsers > 0 ? Math.round((stats.activeUsers / stats.totalUsers) * 100) : 0}%
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '12px',
                    background: 'rgba(139, 92, 246, 0.1)',
                    border: '2px solid rgba(139, 92, 246, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '900',
                    fontSize: '18px',
                    color: '#8b5cf6'
                  }}><HiOutlineStar /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Verified Badges
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#8b5cf6' }}>
                      {stats.verifiedUsers}
                    </div>
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-secondary)' }}>
                    {stats.totalUsers > 0 ? Math.round((stats.verifiedUsers / stats.totalUsers) * 100) : 0}%
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '12px',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '2px solid rgba(34, 197, 94, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '900',
                    fontSize: '18px',
                    color: '#22c55e'
                  }}><HiOutlinePhone /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Phone Verified
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#22c55e' }}>
                      {stats.phoneVerified}
                    </div>
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-secondary)' }}>
                    {stats.totalUsers > 0 ? Math.round((stats.phoneVerified / stats.totalUsers) * 100) : 0}%
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="admin-tabs-row" style={{ display: 'flex', gap: '8px', borderBottom: '2px solid var(--border-color)' }}>
            <button onClick={() => setActiveTab('requests')} style={{
              padding: '14px 28px', 
              background: activeTab === 'requests' ? 'rgba(255, 107, 107, 0.1)' : 'transparent',
              border: 'none', 
              borderBottom: activeTab === 'requests' ? '3px solid #ff6b6b' : '3px solid transparent',
              color: activeTab === 'requests' ? '#ff6b6b' : 'var(--text-secondary)', 
              cursor: 'pointer',
              fontSize: '15px', 
              fontWeight: '700', 
              transition: 'all 0.2s ease',
              borderRadius: '8px 8px 0 0',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Verification Requests
              {stats.verificationRequests > 0 && (
                <span style={{
                  background: '#ef4444',
                  color: 'white',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: '700',
                  minWidth: '20px',
                  textAlign: 'center'
                }}>
                  {stats.verificationRequests}
                </span>
              )}
            </button>
            <button onClick={() => setActiveTab('users')} style={{
              padding: '14px 28px', 
              background: activeTab === 'users' ? 'rgba(255, 107, 107, 0.1)' : 'transparent',
              border: 'none', 
              borderBottom: activeTab === 'users' ? '3px solid #ff6b6b' : '3px solid transparent',
              color: activeTab === 'users' ? '#ff6b6b' : 'var(--text-secondary)', 
              cursor: 'pointer',
              fontSize: '15px', 
              fontWeight: '700', 
              transition: 'all 0.2s ease',
              borderRadius: '8px 8px 0 0'
            }}>
              All Users
            </button>
            <button onClick={() => setActiveTab('admins')} style={{
              padding: '14px 28px', 
              background: activeTab === 'admins' ? 'rgba(255, 107, 107, 0.1)' : 'transparent',
              border: 'none', 
              borderBottom: activeTab === 'admins' ? '3px solid #ff6b6b' : '3px solid transparent',
              color: activeTab === 'admins' ? '#ff6b6b' : 'var(--text-secondary)', 
              cursor: 'pointer',
              fontSize: '15px', 
              fontWeight: '700', 
              transition: 'all 0.2s ease',
              borderRadius: '8px 8px 0 0'
            }}>
              Admin Management
            </button>
            <button onClick={() => setActiveTab('earnings')} style={{
              padding: '14px 28px', 
              background: activeTab === 'earnings' ? 'rgba(255, 107, 107, 0.1)' : 'transparent',
              border: 'none', 
              borderBottom: activeTab === 'earnings' ? '3px solid #ff6b6b' : '3px solid transparent',
              color: activeTab === 'earnings' ? '#ff6b6b' : 'var(--text-secondary)', 
              cursor: 'pointer',
              fontSize: '15px', 
              fontWeight: '700', 
              transition: 'all 0.2s ease',
              borderRadius: '8px 8px 0 0'
            }}>
              Creator Earnings
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="admin-content-wrap" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 40px' }}>
        {error && <div className="error-message" style={{ marginBottom: '24px' }}>{error}</div>}
        {success && <div className="success-message" style={{ marginBottom: '24px' }}>{success}</div>}
      </div>

      {/* Content */}
      <div className="admin-content-wrap" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 40px 40px' }}>
        <div className="profile-section">
          {activeTab === 'users' && (
            <div className="info-card">
              <h3>Filters & Sorting</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Search</label>
                  <input type="text" placeholder="Search by name, email, or phone..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} className="input" style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Sort By</label>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="input" style={{ width: '100%' }}>
                    <option value="subscription">Subscription (Highest First)</option>
                    <option value="name">Name (A-Z)</option>
                    <option value="credits">Credits (Highest First)</option>
                    <option value="joined">Recently Joined</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Gender</label>
                  <select value={filterGender} onChange={(e) => setFilterGender(e.target.value)} className="input" style={{ width: '100%' }}>
                    <option value="ALL">All Genders</option>
                    <option value="FEMALE">Female</option>
                    <option value="MALE">Male</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>Verification Status</label>
                  <select value={filterVerified} onChange={(e) => setFilterVerified(e.target.value)} className="input" style={{ width: '100%' }}>
                    <option value="ALL">All Users</option>
                    <option value="VERIFIED">Verified Only</option>
                    <option value="UNVERIFIED">Unverified Only</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                Showing {filteredUsers.length} of {users.length} users
              </div>
            </div>
          )}
        </div>
      </div>
      {activeTab === 'requests' && (
        <div className="profile-content admin-content-wrap-mobile" style={{ margin: '0 40px 40px' }}>
          <div className="profile-section">
            {verificationRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center', color: '#22c55e' }}><HiOutlineCheckCircle /></div>
                <h3 style={{ margin: '0 0 8px 0' }}>No Pending Requests</h3>
                <p style={{ margin: 0 }}>All verification requests have been reviewed</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {verificationRequests.map((request) => (
                  <div key={request.id} className="info-card" style={{ marginBottom: 0 }}>
                    <div className="admin-card-row admin-card-row--2col" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '24px', alignItems: 'start' }}>
                      <div>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '20px' }}>Verification Request - Video Call Required</h3>
                        <div className="info-grid" style={{ marginTop: '16px' }}>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">User ID</span>
                            <span className="info-value">#{request.user?.id || 'N/A'}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Name</span>
                            <span className="info-value">{request.userName || request.user?.profile?.name || 'N/A'}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Email</span>
                            <span className="info-value">{request.userEmail || request.user?.email || 'N/A'}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Gender</span>
                            <span className="info-value">{request.userGender || request.user?.profile?.gender || 'N/A'}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Requested</span>
                            <span className="info-value">{new Date(request.requestedAt).toLocaleString()}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Status</span>
                            <span className="info-value" style={{
                              padding: '4px 8px', background: '#fbbf24', borderRadius: '6px', fontSize: '12px', fontWeight: '700'
                            }}>{request.status}</span>
                          </div>
                        </div>
                        <div style={{
                          marginTop: '16px', padding: '12px', background: 'rgba(59, 130, 246, 0.1)',
                          borderRadius: '8px', fontSize: '13px', color: 'var(--text-secondary)'
                        }}>
                          <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><HiOutlineVideoCamera aria-hidden="true" /> Video Call Verification:</strong> Click "Start Video Call" to call the user and verify their identity. 
                          Ask them to show a hand gesture (face optional). Once verified, click "Approve" to grant the verification badge.
                        </div>
                      </div>
                      <div className="admin-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '180px' }}>
                        <button 
                          onClick={async () => {
                            try {
                              const token = getToken();
                              const response = await axios.post(
                                `${API_URL}/video-calls/initiate/${request.user?.id}`,
                                {},
                                { headers: { Authorization: `Bearer ${token}` }}
                              );
                              setSuccess('Video call initiated! Waiting for user to answer...');
                              setTimeout(() => setSuccess(''), 3000);
                              // Redirect to active call screen
                              window.location.href = `/active-call?callId=${response.data.id}`;
                            } catch (err) {
                              setError(err.response?.data?.error || 'Failed to initiate call');
                              setTimeout(() => setError(''), 3000);
                            }
                          }}
                          className="btn" 
                          style={{
                            background: '#3b82f6', border: 'none', padding: '12px 20px', fontSize: '14px', fontWeight: '700',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                          }}>
                          <HiOutlineVideoCamera aria-hidden="true" /> Start Video Call
                        </button>
                        <button onClick={() => handleApproveRequest(request.id)} className="btn" style={{
                          background: '#10b981', border: 'none', padding: '12px 20px', fontSize: '14px', fontWeight: '700',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                        }}><HiCheck aria-hidden="true" /> Approve</button>
                        <button onClick={() => handleRejectRequest(request.id)} className="btn" style={{
                          background: '#ef4444', border: 'none', padding: '12px 20px', fontSize: '14px', fontWeight: '700',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                        }}><HiX aria-hidden="true" /> Reject</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="profile-content admin-content-wrap-mobile" style={{ margin: '0 40px 40px' }}>
          <div className="profile-section">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>Loading users...</div>
            ) : users.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center', color: 'var(--text-secondary)' }}><HiOutlineUserGroup /></div>
                <h3 style={{ margin: '0 0 8px 0' }}>No Users Yet</h3>
                <p style={{ margin: 0 }}>No users have been registered in the system</p>
                {error && <p style={{ color: '#ef4444', marginTop: '12px' }}>{error}</p>}
              </div>
            ) : filteredUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                <p>No users match your filters</p>
                <p style={{ fontSize: '14px', marginTop: '8px' }}>Total users: {users.length} | Filtered: {filteredUsers.length}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {filteredUsers.map((user) => (
                  <div key={user.id} className="info-card" style={{ marginBottom: 0 }}>
                    <div className="admin-card-row admin-card-row--3col" style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: '20px', alignItems: 'start' }}>
                      <div>
                        {user.profile?.photoUrls && user.profile.photoUrls.length > 0 ? (
                          <img src={user.profile.photoUrls[0]} alt={user.profile.name} style={{
                            width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--border-light)'
                          }} />
                        ) : (
                          <div style={{
                            width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', fontWeight: '700', color: 'white'
                          }}>
                            {user.profile?.name ? user.profile.name.charAt(0).toUpperCase() : '?'}
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                          <h3 style={{ margin: 0, fontSize: '20px' }}>{user.profile?.name || 'No Name'}</h3>
                          {user.hasVerifiedBadge && (
                            <span style={{
                              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white',
                              padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700',
                              display: 'inline-flex', alignItems: 'center', gap: '4px'
                            }}><HiCheck aria-hidden="true" /> Verified</span>
                          )}
                          <span style={{
                            padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700',
                            background: user.profile?.gender === 'FEMALE' ? 'rgba(236, 72, 153, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                            color: user.profile?.gender === 'FEMALE' ? '#ec4899' : '#3b82f6'
                          }}>{user.profile?.gender || 'N/A'}</span>
                          <span style={{
                            padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700',
                            background: user.active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                            color: user.active ? '#10b981' : '#ef4444'
                          }}>{user.active ? 'Active' : 'Suspended'}</span>
                        </div>
                        <div className="info-grid" style={{ marginTop: '16px' }}>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">User ID</span>
                            <span className="info-value">#{user.id}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Contact</span>
                            <span className="info-value">{user.email || user.phoneNumber || 'N/A'}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Age</span>
                            <span className="info-value">{user.profile?.age || 'N/A'}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Location</span>
                            <span className="info-value">{user.profile?.location || 'N/A'}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Credits</span>
                            <span className="info-value" style={{ 
                              color: user.credits > 10 ? '#22c55e' : user.credits > 0 ? '#f59e0b' : '#ef4444',
                              fontWeight: '700'
                            }}>
                              {user.credits || 0}
                            </span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Joined</span>
                            <span className="info-value">{new Date(user.createdAt).toLocaleDateString()}</span>
                          </div>
                          <div className="info-item" style={{ padding: '12px' }}>
                            <span className="info-label">Role</span>
                            <span className="info-value" style={{ color: user.role === 'ADMIN' ? '#8b5cf6' : 'inherit' }}>{user.role}</span>
                          </div>
                        </div>
                      </div>
                      <div className="admin-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '140px' }}>
                        {user.profile?.gender === 'MALE' && (
                          <button onClick={() => {
                            setUserToCredit(user);
                            setCreditsAction('add');
                            setCreditsAmount('');
                            setShowCreditsModal(true);
                          }} className="btn" style={{
                            background: '#8b5cf6', border: 'none', padding: '10px 16px', fontSize: '13px', fontWeight: '700',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                          }}><HiOutlineCurrencyDollar aria-hidden="true" /> Manage Credits</button>
                        )}
                        {user.profile?.gender === 'FEMALE' && (
                          <>
                            <button onClick={() => {
                              setUserToBonus(user);
                              setBonusAmount('');
                              setBonusDescription('');
                              setShowBonusModal(true);
                            }} className="btn" style={{
                              background: '#10b981', border: 'none', padding: '10px 16px', fontSize: '13px', fontWeight: '700',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                            }}><HiOutlineGift aria-hidden="true" /> Credit Bonus</button>
                            {user.hasVerifiedBadge ? (
                              <button onClick={() => {
                                setUserToRevoke(user);
                                setShowRevokeModal(true);
                              }} className="btn" style={{
                                background: '#f59e0b', border: 'none', padding: '10px 16px', fontSize: '13px'
                              }}>Remove Badge</button>
                            ) : (
                              <button onClick={() => {
                                setUserToVerify(user);
                                setShowVerificationModal(true);
                              }} className="btn" style={{
                                background: '#10b981', border: 'none', padding: '10px 16px', fontSize: '13px',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                              }}><HiOutlineBadgeCheck aria-hidden="true" /> Verify User</button>
                            )}
                          </>
                        )}
                        <button onClick={() => handleToggleActive(user.id, user.active)} className="btn" style={{
                          background: user.active ? '#f59e0b' : '#10b981', border: 'none', padding: '10px 16px', fontSize: '13px'
                        }}>{user.active ? 'Suspend' : 'Activate'}</button>
                        {user.role === 'USER' && (
                          <button onClick={() => {
                            setUserToPromote(user);
                            setShowPromoteModal(true);
                          }} className="btn" style={{
                            background: '#8b5cf6', border: 'none', padding: '10px 16px', fontSize: '13px'
                          }}>Promote to Admin</button>
                        )}
                        <button onClick={() => {
                          setUserToDelete(user);
                          setShowDeleteModal(true);
                        }} className="btn" style={{
                          background: '#ef4444', border: 'none', padding: '10px 16px', fontSize: '13px'
                        }}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin Management Tab */}
      {activeTab === 'admins' && (
        <div className="admin-content-wrap" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 40px 40px' }}>
          <div className="profile-section">
            <div className="info-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h3>Admin Management</h3>
                <button 
                  onClick={() => setShowInviteAdminModal(true)}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8787 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)'
                  }}
                >
                  + Invite Admin
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                      <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)' }}>Email</th>
                      <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)' }}>Role</th>
                      <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)' }}>Status</th>
                      <th style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)' }}>Created</th>
                      <th style={{ padding: '16px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map(admin => (
                      <tr key={admin.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '16px', fontSize: '14px', fontWeight: '600' }}>{admin.email}</td>
                        <td style={{ padding: '16px' }}>
                          <select
                            value={admin.role}
                            onChange={(e) => handleUpdateAdminRole(admin.id, e.target.value)}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid var(--border-color)',
                              background: 'var(--bg-secondary)',
                              color: 'var(--text-primary)',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="MODERATOR">Moderator</option>
                            <option value="ADMIN">Admin</option>
                            <option value="SUPER_ADMIN">Super Admin</option>
                          </select>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '700',
                            background: admin.active ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            color: admin.active ? '#22c55e' : '#ef4444'
                          }}>
                            {admin.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                          {new Date(admin.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'right' }}>
                          <button
                            onClick={() => handleDeactivateAdmin(admin.id)}
                            style={{
                              padding: '8px 16px',
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: '#ef4444',
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              borderRadius: '8px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Deactivate
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {admins.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                    No admins found. Invite your first admin!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Creator Earnings Tab */}
      {activeTab === 'earnings' && (
        <div className="admin-content-wrap" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 40px 40px' }}>
          <div className="profile-section">
            <div className="info-card">
              <h3 style={{ marginBottom: '24px' }}>Creator Earnings Management</h3>
              
              {loadingEarnings ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                  Loading earnings...
                </div>
              ) : earnings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', display: 'flex', justifyContent: 'center', color: 'var(--text-secondary)' }}><HiOutlineCash /></div>
                  <h3 style={{ margin: '0 0 8px 0' }}>No Earnings Yet</h3>
                  <p style={{ margin: 0 }}>No creators have earned any credits yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {earnings.map((earning) => (
                    <div key={earning.creatorId} className="info-card" style={{ marginBottom: 0, background: 'var(--bg-secondary)' }}>
                      <div className="admin-card-row admin-card-row--3col" style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: '20px', alignItems: 'start' }}>
                        {/* Creator Photo */}
                        <div>
                          {earning.creatorPhoto ? (
                            <img 
                              src={earning.creatorPhoto} 
                              alt={earning.creatorName}
                              style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '3px solid var(--border-light)'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '32px',
                              fontWeight: '700',
                              color: 'white'
                            }}>
                              {earning.creatorName ? earning.creatorName.charAt(0).toUpperCase() : '?'}
                            </div>
                          )}
                        </div>
                        
                        {/* Creator Info & Earnings */}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '20px' }}>{earning.creatorName}</h3>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '700',
                              background: 'rgba(236, 72, 153, 0.15)',
                              color: '#ec4899',
                              display: 'inline-flex', alignItems: 'center', gap: '4px'
                            }}><HiOutlineSparkles aria-hidden="true" /> Creator</span>
                          </div>
                          
                          <div className="info-grid" style={{ marginTop: '12px' }}>
                            <div className="info-item" style={{ padding: '12px' }}>
                              <span className="info-label">Email</span>
                              <span className="info-value">{earning.creatorEmail}</span>
                            </div>
                            <div className="info-item" style={{ padding: '12px', background: 'rgba(251, 191, 36, 0.1)' }}>
                              <span className="info-label">Pending Earnings</span>
                              <span className="info-value" style={{ color: '#fbbf24', fontWeight: '800', fontSize: '18px' }}>
                                {earning.pendingEarnings} credits
                              </span>
                            </div>
                            <div className="info-item" style={{ padding: '12px', background: 'rgba(34, 197, 94, 0.1)' }}>
                              <span className="info-label">Paid Earnings</span>
                              <span className="info-value" style={{ color: '#22c55e', fontWeight: '800', fontSize: '18px' }}>
                                {earning.paidEarnings} credits
                              </span>
                            </div>
                            <div className="info-item" style={{ padding: '12px', background: 'rgba(139, 92, 246, 0.1)' }}>
                              <span className="info-label">Total Earnings</span>
                              <span className="info-value" style={{ color: '#8b5cf6', fontWeight: '800', fontSize: '18px' }}>
                                {earning.totalEarnings} credits
                              </span>
                            </div>
                          </div>
                          
                          {/* Pending Details */}
                          {earning.pendingDetails && earning.pendingDetails.length > 0 && (
                            <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(251, 191, 36, 0.05)', borderRadius: '8px', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
                              <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '8px', color: '#fbbf24' }}>
                                Pending Transactions ({earning.pendingDetails.length})
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {earning.pendingDetails.slice(0, 3).map((detail) => (
                                  <div key={detail.id} style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{detail.source} - {new Date(detail.createdAt).toLocaleDateString()}</span>
                                    <span style={{ fontWeight: '700', color: '#fbbf24' }}>{detail.amount} credits</span>
                                  </div>
                                ))}
                                {earning.pendingDetails.length > 3 && (
                                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                    +{earning.pendingDetails.length - 3} more transactions
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="admin-card-actions" style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '160px' }}>
                          {earning.pendingEarnings > 0 && (
                            <button
                              onClick={() => {
                                // Find first pending earning to process
                                if (earning.pendingDetails && earning.pendingDetails.length > 0) {
                                  setEarningToPayout(earning.pendingDetails[0]);
                                  setShowPayoutModal(true);
                                }
                              }}
                              className="btn"
                              style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                border: 'none',
                                padding: '12px 20px',
                                fontSize: '14px',
                                fontWeight: '700',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                              }}
                            >
                              <HiOutlineCash aria-hidden="true" /> Process Payout
                            </button>
                          )}
                          <button
                            onClick={() => window.location.href = `/creator/${earning.creatorId}`}
                            className="btn"
                            style={{
                              background: 'rgba(139, 92, 246, 0.1)',
                              border: '2px solid rgba(139, 92, 246, 0.3)',
                              color: '#8b5cf6',
                              padding: '12px 20px',
                              fontSize: '14px',
                              fontWeight: '700'
                            }}
                          >
                            View Profile
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && userToVerify && (
        <div className="modal-overlay" onClick={() => setShowVerificationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Grant Verification Badge</h3>
            <p>Grant verification badge to {userToVerify.email}?</p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowVerificationModal(false);
                  setUserToVerify(null);
                }}
                disabled={approving}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => handleApproveVerification(userToVerify.id)}
                disabled={approving}
                style={{ background: '#10b981' }}
              >
                {approving ? 'Granting...' : 'Grant Badge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Verification Modal */}
      {showRevokeModal && userToRevoke && (
        <div className="modal-overlay" onClick={() => setShowRevokeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Remove Verification Badge</h3>
            <p>Remove verification badge from {userToRevoke.email}?</p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowRevokeModal(false);
                  setUserToRevoke(null);
                }}
                disabled={approving}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => handleRevokeVerification(userToRevoke.id)}
                disabled={approving}
                style={{ background: '#f59e0b' }}
              >
                {approving ? 'Removing...' : 'Remove Badge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && userToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete User</h3>
            <p>Are you sure you want to delete {userToDelete.email}? This action cannot be undone!</p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                disabled={approving}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => handleDeleteUser(userToDelete.id)}
                disabled={approving}
                style={{ background: '#ef4444' }}
              >
                {approving ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Credits Modal */}
      {showCreditsModal && userToCredit && (
        <div className="modal-overlay" onClick={() => setShowCreditsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><HiOutlineCurrencyDollar aria-hidden="true" /> Manage Credits</h3>
            <p style={{ marginBottom: '20px' }}>
              Manage credits for <strong>{userToCredit.email}</strong>
              <br />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Current balance: <strong style={{ color: '#8b5cf6' }}>{userToCredit.credits || 0} credits</strong>
              </span>
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Action
              </label>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button
                  onClick={() => setCreditsAction('add')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: `2px solid ${creditsAction === 'add' ? '#8b5cf6' : 'var(--border-color)'}`,
                    background: creditsAction === 'add' ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    color: creditsAction === 'add' ? '#8b5cf6' : 'var(--text-primary)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <HiOutlinePlus aria-hidden="true" /> Add Credits
                  </span>
                </button>
                <button
                  onClick={() => setCreditsAction('set')}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: `2px solid ${creditsAction === 'set' ? '#8b5cf6' : 'var(--border-color)'}`,
                    background: creditsAction === 'set' ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    color: creditsAction === 'set' ? '#8b5cf6' : 'var(--text-primary)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <HiOutlineAdjustments aria-hidden="true" /> Set Credits
                  </span>
                </button>
              </div>
              
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                {creditsAction === 'add' ? 'Credits to Add' : 'New Credit Balance'}
              </label>
              <input
                type="number"
                min="0"
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(e.target.value)}
                placeholder={creditsAction === 'add' ? 'e.g., 100' : 'e.g., 500'}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              />
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                {creditsAction === 'add' 
                  ? `New balance will be: ${(userToCredit.credits || 0) + (parseInt(creditsAmount) || 0)} credits`
                  : `Current balance: ${userToCredit.credits || 0} credits`
                }
              </p>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowCreditsModal(false);
                  setUserToCredit(null);
                  setCreditsAmount('');
                }}
                disabled={approving}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleManageCredits}
                disabled={approving || !creditsAmount}
                style={{ background: '#8b5cf6' }}
              >
                {approving ? 'Updating...' : (creditsAction === 'add' ? 'Add Credits' : 'Set Credits')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Admin Modal */}
      {showInviteAdminModal && (
        <div className="modal-overlay" onClick={() => !inviting && setShowInviteAdminModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h3 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '800' }}>Invite Admin</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                Email Address
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="admin@example.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                Role
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="MODERATOR">Moderator - Can moderate content</option>
                <option value="ADMIN">Admin - Can approve users & verifications</option>
                <option value="SUPER_ADMIN">Super Admin - Full access</option>
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                Temporary Password
              </label>
              <input
                type="text"
                value={inviteTempPassword}
                onChange={(e) => setInviteTempPassword(e.target.value)}
                placeholder="Enter temporary password"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px'
                }}
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Admin will receive this password via email and should change it after first login
              </small>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowInviteAdminModal(false)}
                disabled={inviting}
                style={{
                  padding: '12px 24px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: inviting ? 'not-allowed' : 'pointer',
                  opacity: inviting ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleInviteAdmin}
                disabled={inviting}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8787 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: inviting ? 'not-allowed' : 'pointer',
                  opacity: inviting ? 0.5 : 1,
                  boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)'
                }}
              >
                {inviting ? 'Inviting...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promote User Modal */}
      {showPromoteModal && userToPromote && (
        <div className="modal-overlay" onClick={() => !promoting && setShowPromoteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h3 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '800' }}>Promote User to Admin</h3>
            
            <div style={{ marginBottom: '20px', padding: '16px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '12px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)' }}>
                <strong>User:</strong> {userToPromote.email}
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                Admin Role
              </label>
              <select
                value={promoteRole}
                onChange={(e) => setPromoteRole(e.target.value)}
                disabled={promoting}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border-color)',
                  borderRadius: '12px',
                  fontSize: '14px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="MODERATOR">Moderator</option>
                <option value="ADMIN">Admin</option>
                <option value="SUPER_ADMIN">Super Admin</option>
              </select>
              <p style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                • Moderator: Can manage users and content<br/>
                • Admin: Full access to admin panel<br/>
                • Super Admin: Can manage other admins
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPromoteModal(false)}
                disabled={promoting}
                style={{
                  padding: '12px 24px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '2px solid var(--border-color)',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: promoting ? 'not-allowed' : 'pointer',
                  opacity: promoting ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePromoteUser}
                disabled={promoting}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: promoting ? 'not-allowed' : 'pointer',
                  opacity: promoting ? 0.5 : 1,
                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
                }}
              >
                {promoting ? 'Promoting...' : 'Promote User'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Payout Modal */}
      {showPayoutModal && earningToPayout && (
        <div className="modal-overlay" onClick={() => !processingPayout && setShowPayoutModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '56px', marginBottom: '12px', display: 'flex', justifyContent: 'center', color: '#10b981' }}><HiOutlineCash /></div>
              <h2 style={{ marginBottom: '8px', color: '#10b981' }}>Process Payout</h2>
            </div>
            
            <div style={{ 
              background: 'rgba(16, 185, 129, 0.1)', 
              padding: '16px', 
              borderRadius: '12px',
              marginBottom: '20px',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '600' }}>
                Payout Details:
              </p>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                <div style={{ marginBottom: '4px' }}>
                  <strong>Amount:</strong> <span style={{ color: '#10b981', fontWeight: '800' }}>{earningToPayout.amount} credits</span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <strong>Source:</strong> {earningToPayout.source}
                </div>
                <div>
                  <strong>Date:</strong> {new Date(earningToPayout.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="payout-method" style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600' }}>
                Payout method
              </label>
              <input
                id="payout-method"
                type="text"
                className="input"
                placeholder="e.g., Bank Transfer, PayPal, Cash"
                value={payoutMethod}
                onChange={(e) => setPayoutMethod(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="payout-details" style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600' }}>
                Payout details
              </label>
              <input
                id="payout-details"
                type="text"
                className="input"
                placeholder="e.g., account number, reference"
                value={payoutDetails}
                onChange={(e) => setPayoutDetails(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: '1.6' }}>
              The creator will receive an email notification once the payout is processed.
            </p>

            {error && (
              <div className="error" style={{ 
                padding: '12px', 
                backgroundColor: '#fee2e2', 
                color: '#dc2626',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowPayoutModal(false);
                  setEarningToPayout(null);
                  setPayoutMethod('');
                  setPayoutDetails('');
                  setError('');
                }}
                disabled={processingPayout}
              >
                Cancel
              </button>
              <button 
                className="btn" 
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
                onClick={handlePayoutEarning}
                disabled={processingPayout}
              >
                {processingPayout ? 'Processing...' : 'Process Payout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Bonus Modal */}
      {showBonusModal && userToBonus && (
        <div className="modal-overlay" onClick={() => setShowBonusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><HiOutlineGift aria-hidden="true" /> Credit Bonus</h3>
            <p style={{ marginBottom: '20px' }}>
              Credit a bonus to <strong>{userToBonus.profile?.name || userToBonus.email}</strong>
              <br />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                This will be added to their pending earnings for payout
              </span>
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Bonus Amount (Credits)
              </label>
              <input
                type="number"
                min="1"
                value={bonusAmount}
                onChange={(e) => setBonusAmount(e.target.value)}
                placeholder="e.g., 100"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '16px'
                }}
              />
              
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Description / Reason
              </label>
              <textarea
                value={bonusDescription}
                onChange={(e) => setBonusDescription(e.target.value)}
                placeholder="e.g., Welcome bonus, Promotion reward, Top performer bonus"
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <HiOutlineLightBulb aria-hidden="true" style={{ flexShrink: 0 }} /> This description will be shown to the user and in earnings records
              </p>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowBonusModal(false);
                  setUserToBonus(null);
                  setBonusAmount('');
                  setBonusDescription('');
                }}
                disabled={creditingBonus}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCreditBonus}
                disabled={creditingBonus || !bonusAmount || !bonusDescription}
                style={{ background: '#10b981' }}
              >
                {creditingBonus ? 'Crediting...' : 'Credit Bonus'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm: approve verification request */}
      <ConfirmModal
        open={requestToApprove !== null}
        title="Approve verification request?"
        message="This will grant the user a verified badge."
        confirmLabel="Approve"
        loading={approvingRequest}
        onConfirm={confirmApproveRequest}
        onCancel={() => !approvingRequest && setRequestToApprove(null)}
      />

      {/* Prompt: reject verification request with a reason */}
      <ConfirmModal
        open={requestToReject !== null}
        title="Reject verification request"
        message="Let the user know why their request was rejected."
        confirmLabel="Reject request"
        danger
        loading={rejectingRequest}
        withInput
        inputLabel="Reason"
        inputPlaceholder="e.g., Photo didn't match, request unclear"
        inputValue={rejectReason}
        onInputChange={setRejectReason}
        confirmDisabled={!rejectReason.trim()}
        onConfirm={confirmRejectRequest}
        onCancel={() => !rejectingRequest && setRequestToReject(null)}
      />

      {/* Confirm: deactivate admin */}
      <ConfirmModal
        open={adminToDeactivate !== null}
        title="Deactivate this admin?"
        message="They will immediately lose admin access. You can re-invite them later."
        confirmLabel="Deactivate"
        danger
        loading={deactivatingAdmin}
        onConfirm={confirmDeactivateAdmin}
        onCancel={() => !deactivatingAdmin && setAdminToDeactivate(null)}
      />
    </div>
  );
};

export default AdminPanel;
